import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Card,
  Row,
  Col,
  Tag,
  Popconfirm,
  Divider,
  Result
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LockOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import PermissionsModal from '../components/Common/PermissionsModal';
import {
  getAllUsers,
  createUser,
  updateUser,
  deleteUser,
  checkEmailExists
} from '../services/userService';
import { logActivity } from '../services/auditService';
import { ACTIVITY_TYPES } from '../models/activityTypes';
import './ManageUsersPage.css';

const ManageUsersPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [permissionsModalVisible, setPermissionsModalVisible] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);

  // Vérifier que l'utilisateur est admin au montage du composant
  useEffect(() => {
    if (!authLoading && user?.role !== 'admin') {
      message.error('Accès refusé: seuls les administrateurs peuvent gérer les utilisateurs');
      navigate('/');
    }
  }, [authLoading, user, navigate]);

  // Load users on component mount
  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    setLoading(true);
    const result = await getAllUsers();
    if (result.success) {
      setUsers(result.data || []);
    } else {
      message.error('Erreur lors du chargement des utilisateurs');
    }
    setLoading(false);
  };

  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      password: user.password,
      role: user.role,
      status: user.status
    });
    setModalVisible(true);
  };

  const handleDeleteUser = async (id, email) => {
    const result = await deleteUser(id);
    if (result.success) {
      // Log the activity
      await logActivity(
        ACTIVITY_TYPES.USER_DELETED,
        user.id,
        'user',
        id,
        { email },
        `Utilisateur ${email} supprimé`
      );
      
      message.success(`L'utilisateur ${email} a été supprimé`);
      loadUsers();
    } else {
      message.error('Erreur lors de la suppression');
    }
  };

  const handleOpenPermissionsModal = (userRecord) => {
    setSelectedUserForPermissions(userRecord);
    setPermissionsModalVisible(true);
  };

  const handlePermissionsSuccess = () => {
    loadUsers();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingUser(null);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      // Check if email already exists (for new users or if email changed)
      if (!editingUser || editingUser.email !== values.email) {
        const emailCheck = await checkEmailExists(values.email, editingUser?.id);
        if (emailCheck.success && emailCheck.exists) {
          message.error('Cet email est déjà utilisé');
          return;
        }
      }

      let result;
      if (editingUser) {
        // Update existing user
        result = await updateUser(editingUser.id, values);
        if (result.success) {
          // Log the activity
          const changes = {};
          if (editingUser.email !== values.email) changes.email = { old: editingUser.email, new: values.email };
          if (editingUser.firstName !== values.firstName) changes.firstName = { old: editingUser.firstName, new: values.firstName };
          if (editingUser.lastName !== values.lastName) changes.lastName = { old: editingUser.lastName, new: values.lastName };
          if (editingUser.role !== values.role) changes.role = { old: editingUser.role, new: values.role };
          if (editingUser.status !== values.status) changes.status = { old: editingUser.status, new: values.status };
          
          await logActivity(
            ACTIVITY_TYPES.USER_UPDATED,
            user.id,
            'user',
            editingUser.id,
            changes,
            `Utilisateur ${values.email} modifié`
          );
          
          message.success('Utilisateur modifié avec succès');
        }
      } else {
        // Create new user
        result = await createUser(values);
        if (result.success) {
          // Log the activity
          await logActivity(
            ACTIVITY_TYPES.USER_CREATED,
            user.id,
            'user',
            result.data.id,
            { 
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              role: values.role,
              status: values.status
            },
            `Nouvel utilisateur créé: ${values.email}`
          );
          
          message.success('Utilisateur créé avec succès');
        }
      }

      if (result.success) {
        setModalVisible(false);
        form.resetFields();
        loadUsers();
      } else {
        message.error(result.error?.message || 'Erreur lors de l\'opération');
      }
    } catch (error) {
      // Gestion silencieuse de l'erreur
    }
  };

  const columns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '25%',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Nom Complet',
      key: 'fullName',
      width: '20%',
      render: (_, record) => `${record.firstName} ${record.lastName}`
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: '12%',
      render: (role) => {
        const colors = {
          admin: 'red',
          operator: 'blue',
          viewer: 'green'
        };
        return <Tag color={colors[role] || 'default'}>{role}</Tag>;
      }
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => (
        <Tag color={status === 'active' ? 'success' : 'error'}>
          {status === 'active' ? 'Actif' : 'Inactif'}
        </Tag>
      )
    },
    {
      title: 'Dernière Connexion',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      width: '15%',
      render: (date) => 
        date ? new Date(date).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }) : 'Jamais'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '16%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
          >
            Modifier
          </Button>
          <Button
            type="default"
            size="small"
            icon={<LockOutlined />}
            onClick={() => handleOpenPermissionsModal(record)}
          >
            Permissions
          </Button>
          <Popconfirm
            title="Supprimer l'utilisateur"
            description={`Êtes-vous sûr de vouloir supprimer ${record.email}?`}
            onConfirm={() => handleDeleteUser(record.id, record.email)}
            okText="Oui"
            cancelText="Non"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Si l'utilisateur n'est pas admin, afficher un message d'erreur
  if (!authLoading && user?.role !== 'admin') {
    return (
      <Result
        status="403"
        title="Accès Refusé"
        subTitle="Seuls les administrateurs peuvent gérer les utilisateurs."
      />
    );
  }

  return (
    <div className="manage-users-page">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h1>Gestion des Utilisateurs</h1>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddUser}
              size="large"
            >
              Ajouter un Utilisateur
            </Button>
          </div>
        </Col>

        <Col span={24}>
          <Card>
            <Table
              columns={columns}
              dataSource={users}
              loading={loading}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total: ${total} utilisateurs`
              }}
              locale={{
                emptyText: 'Aucun utilisateur trouvé'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Modal for Add/Edit User */}
      <Modal
        title={editingUser ? 'Modifier l\'utilisateur' : 'Ajouter un nouvel utilisateur'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={500}
        okText={editingUser ? 'Modifier' : 'Créer'}
        cancelText="Annuler"
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Veuillez entrer un email' },
              { type: 'email', message: 'Email invalide' }
            ]}
          >
            <Input type="email" placeholder="utilisateur@example.com" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="Prénom"
                rules={[{ required: true, message: 'Veuillez entrer le prénom' }]}
              >
                <Input placeholder="Prénom" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Nom"
                rules={[{ required: true, message: 'Veuillez entrer le nom' }]}
              >
                <Input placeholder="Nom" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="password"
            label="Mot de passe"
            rules={[
              {
                required: !editingUser,
                message: 'Veuillez entrer un mot de passe'
              },
              {
                min: 6,
                message: 'Le mot de passe doit contenir au moins 6 caractères'
              }
            ]}
          >
            <Input.Password
              type={showPassword ? 'text' : 'password'}
              placeholder="Entrez un mot de passe sécurisé"
              suffix={
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer', color: '#999' }}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </span>
              }
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="Rôle"
                rules={[{ required: true, message: 'Veuillez sélectionner un rôle' }]}
                initialValue="operator"
              >
                <Select>
                  <Select.Option value="admin">Administrateur</Select.Option>
                  <Select.Option value="operator">Opérateur</Select.Option>
                  <Select.Option value="viewer">Lecteur</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Statut"
                rules={[{ required: true, message: 'Veuillez sélectionner un statut' }]}
                initialValue="active"
              >
                <Select>
                  <Select.Option value="active">Actif</Select.Option>
                  <Select.Option value="inactive">Inactif</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <div style={{ fontSize: '12px', color: '#999' }}>
              {editingUser && (
                <p>Utilisateur créé le: {new Date(editingUser.createdAt).toLocaleDateString('fr-FR')}</p>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <PermissionsModal
        visible={permissionsModalVisible}
        onClose={() => setPermissionsModalVisible(false)}
        user={selectedUserForPermissions}
        onSuccess={handlePermissionsSuccess}
      />
    </div>
  );
};

export default ManageUsersPage;
